import { put } from 'redux-saga/effects';

import { store } from '../store';
import { send } from '../../helpers/requests';
import * as actions from './actions';
import * as EntriesActions from '../TimesheetEntries/actions';
import * as ClusterAssignModalActions from '../ClusterAssignModal/actions';
import AnalyticsClient from '../../analytics/analyticsClient';
import trackEvents from '../../analytics/trackEvents';
import { selectToken } from '../selectors';

export function* deleteClusterNodeRequestSaga({ data }) {
  try {
    const res = yield send({
      url: `/clusters/${data.id}/split`,
      method: 'POST',
      data: { nodesToRemove: [data.nodeId], action: 'split' },
      token: selectToken(store.getState()),
    });
    yield put(actions.deleteClusterNodeRequestSuccess({ clusterId: data.id, nodeId: data.nodeId, res }));
    yield put(
      actions.updateClusterRequest({
        id: res.id,
        data: {
          timesheet_entry_id: null,
          client_id: null,
          project_id: null,
          task_id: null,
          item_id: null,
          miscellaneousReason: 'deleted',
        },
      })
    );
    AnalyticsClient.track(trackEvents.ACTIVITY_NODE_DELETE);
  } catch (error) {
    yield put(actions.splitClusterAndAssignToEntryRequestFailure(error));
  }
}

export function* getClustersRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/clusters/timesheet/${payload.data.id}`,
      method: 'GET',
      token: selectToken(store.getState()),
    });
    yield put(actions.getClustersRequestSuccess(res));
  } catch (error) {
    yield put(actions.getClustersRequestFailure(error));
  }
}

export function* splitClusterRequestSaga({ data }) {
  try {
    const res = yield send({
      url: `/clusters/${data.id}/split`,
      method: 'POST',
      data: { nodesToRemove: data.nodesToRemove, action: data.action },
      token: selectToken(store.getState()),
    });
    yield put(actions.splitClusterRequestSuccess({ clusterId: data.id, nodeIds: [...data.nodesToRemove], res }));
    AnalyticsClient.track(trackEvents.ACTIVITY_SPLIT);
  } catch (error) {
    yield put(actions.splitClusterRequestFailure(error));
  }
}

export function* splitClusterAndAssignToEntryRequestSaga({ data }) {
  try {
    const res = yield send({
      url: `/clusters/${data.id}/split`,
      method: 'POST',
      data: { nodesToRemove: data.nodesToRemove, action: data.action },
      token: selectToken(store.getState()),
    });
    yield put(actions.splitClusterAndAssignToEntryRequestSuccess({ clusterId: data.id, nodeIds: [...data.nodesToRemove], res }));
    const splittedDuration =
      parseInt(res.duration, 10) ||
      (res.nodes &&
        res.nodes.reduce((acc, cur) => {
          return acc + parseInt(cur.duration, 10);
        }, 0)) ||
      0;
    yield put(
      EntriesActions.updateTimesheetEntryRequest({
        timesheetId: store.getState().timesheet.data.id,
        entryId: data.existingEntry.id,
        data: {
          duration: parseInt(data.existingEntry.duration || 0, 10) + splittedDuration,
        },
      })
    );
    yield put(
      actions.updateClusterRequest({
        id: res.id,
        data: {
          miscellaneousReason: null,
          predictedClientId: data.existingEntry.confirmedClientId,
          predictedProjectId: data.existingEntry.confirmedProjectId,
          predictedTaskId: data.existingEntry.confirmedTaskId,
          timesheet_entry_id: data.existingEntry.id,
        },
      })
    );
    AnalyticsClient.track(trackEvents.ACTIVITY_SPLIT);
  } catch (error) {
    yield put(actions.splitClusterAndAssignToEntryRequestFailure(error));
  }
}

export function* splitClusterAndOpenClusterAssignModalSaga({ data }) {
  try {
    if (data.nodesToRemove.length === 0) return;
    const res = yield send({
      url: `/clusters/${data.id}/split`,
      method: 'POST',
      data: { nodesToRemove: data.nodesToRemove, action: data.action },
      token: selectToken(store.getState()),
    });
    yield put(actions.splitClusterRequestSuccess({ clusterId: data.id, nodeIds: [...data.nodesToRemove], res }));
    yield put(ClusterAssignModalActions.showClusterAssignModal({ cluster: res, item: {} }));
  } catch (error) {
    yield put(actions.splitClusterRequestFailure(error));
  }
}

export function* updateClusterNodeRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/clusters/${payload.data.clusterId}/nodes/${payload.data.nodeId}`,
      method: 'PUT',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.updateClusterNodeRequestSuccess({ clusterId: payload.data.clusterId, nodeId: payload.data.nodeId, res }));
    AnalyticsClient.track(trackEvents.ACTIVITY_UPDATE);
  } catch (error) {
    yield put(actions.updateClusterNodeRequestFailure(error));
  }
}

export function* updateClusterOverlapsRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/clusters/nodes/overlaps`,
      method: 'PUT',
      data: payload.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.updateClusterOverlapsRequestSuccess(res));
  } catch (error) {
    yield put(actions.updateClusterOverlapsRequestFailure(error));
  }
}

export function* updateClusterRequestSaga(payload) {
  try {
    /*
        User labels the card with a miscellaneous_reason, therefore the
        nodes that are overlapping should now be surfaced
      */
    const overlapsToUpdate = store
      .getState()
      .clusters.data.reduce((p, c) => p.concat(c.nodes), [])
      .reduce((p, c) => p.concat(c.overlaps), [])
      .filter((d) => d && d.overlapping_cluster_id === payload.data.id);

    if (overlapsToUpdate.length > 0) {
      try {
        const res = yield send({
          url: `/clusters/nodes/overlaps`,
          method: 'PUT',
          data: { overlap_ids: overlapsToUpdate.map((d) => d.id), data: { isOverlapping: false } },
          token: selectToken(store.getState()),
        });
        yield put(actions.updateClusterOverlapsRequestSuccess({ res, overlapsToUpdate }));
      } catch (error) {
        yield put(actions.updateClusterOverlapsRequestFailure(error));
        throw error;
      }
    }

    const res = yield send({
      url: `/clusters/${payload.data.id}`,
      method: 'PUT',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.updateClusterRequestSuccess(res));
  } catch (error) {
    yield put(actions.updateClusterRequestFailure(error));
  }
}
